// src/utils/googleDocumentGenerator.js
// Document generator using Google Drive API

import { saveAs } from 'file-saver';
import { supabase } from '../api/supabase';
import {
  isGoogleAuthenticated,
  generateDocumentFromTemplate,
  getGoogleAuthUrl,
  getDocumentText,
  replaceTextInDoc,
  copyGoogleDoc,
  deleteFile,
  createFolder,
} from '../api/googleDrive';

// Google Drive Template IDs (configure these in .env or update here)
const TEMPLATE_IDS = {
  // Yacht templates
  'contrat-yacht': process.env.REACT_APP_GOOGLE_TEMPLATE_CONTRAT_YACHT || '1eKe6urCbVRjj6bN94SCXIDf44-O3TJc-5Iq97XdooZ4',
  'confirmation-yacht': process.env.REACT_APP_GOOGLE_TEMPLATE_CONFIRMATION_YACHT,
  'facturation-yacht': process.env.REACT_APP_GOOGLE_TEMPLATE_FACTURATION_YACHT,

  // Seabob templates
  'contrat-seabob': process.env.REACT_APP_GOOGLE_TEMPLATE_CONTRAT_SEABOB,
  'confirmation-seabob': process.env.REACT_APP_GOOGLE_TEMPLATE_CONFIRMATION_SEABOB,
  'facturation-seabob': process.env.REACT_APP_GOOGLE_TEMPLATE_FACTURATION_SEABOB,

  // Jetski templates
  'contrat-jetski': process.env.REACT_APP_GOOGLE_TEMPLATE_CONTRAT_JETSKI,
  'confirmation-jetski': process.env.REACT_APP_GOOGLE_TEMPLATE_CONFIRMATION_JETSKI,
  'facturation-jetski': process.env.REACT_APP_GOOGLE_TEMPLATE_FACTURATION_JETSKI,

  // Lunch templates
  'contrat-lunch': process.env.REACT_APP_GOOGLE_TEMPLATE_CONTRAT_LUNCH,
  'confirmation-lunch': process.env.REACT_APP_GOOGLE_TEMPLATE_CONFIRMATION_LUNCH,
  'facture-lunch': process.env.REACT_APP_GOOGLE_TEMPLATE_FACTURE_LUNCH,

  // Custom templates
  'contrat-custom': process.env.REACT_APP_GOOGLE_TEMPLATE_CONTRAT_CUSTOM,
  'confirmation-custom': process.env.REACT_APP_GOOGLE_TEMPLATE_CONFIRMATION_CUSTOM,
  'facturation-custom': process.env.REACT_APP_GOOGLE_TEMPLATE_FACTURATION_CUSTOM,
};

// Google Drive folder for generated documents
const OUTPUT_FOLDER_ID = process.env.REACT_APP_GOOGLE_OUTPUT_FOLDER_ID;

// In-memory map of templateKey -> backup Google Doc ID (persisted in localStorage)
const BACKUP_IDS_KEY = 'google_template_backup_ids';
const BACKUP_FOLDER_ID_KEY = 'google_template_backup_folder_id';

/**
 * Get or create the backup folder in Google Drive.
 * Folder ID is cached in localStorage so we only create it once.
 */
const getOrCreateBackupFolder = async () => {
  const cached = localStorage.getItem(BACKUP_FOLDER_ID_KEY);
  if (cached) {
    console.log(`[Backup] Using cached backup folder: ${cached}`);
    return cached;
  }

  if (process.env.REACT_APP_GOOGLE_BACKUP_FOLDER_ID) {
    console.log(`[Backup] Using env backup folder: ${process.env.REACT_APP_GOOGLE_BACKUP_FOLDER_ID}`);
    localStorage.setItem(BACKUP_FOLDER_ID_KEY, process.env.REACT_APP_GOOGLE_BACKUP_FOLDER_ID);
    return process.env.REACT_APP_GOOGLE_BACKUP_FOLDER_ID;
  }

  console.log(`[Backup] No folder found — creating one inside OUTPUT_FOLDER_ID: ${OUTPUT_FOLDER_ID}`);
  const folder = await createFolder('sys_bk_' + Math.random().toString(36).substring(2, 8), OUTPUT_FOLDER_ID);
  console.log(`[Backup] Backup folder created → id: ${folder.id}, name: ${folder.name}`);
  localStorage.setItem(BACKUP_FOLDER_ID_KEY, folder.id);
  return folder.id;
};

// Required {{variables}} per template — if any are missing, the template was modified
const REQUIRED_VARIABLES = {
  'contrat-yacht': [
    'today_date', 'generation_date', 'charter_date', 'from_hour', 'to_hour',
    'client_name', 'charterer', 'brand_of_vessel', 'name_of_vessel', 'length',
    'cruising_area', 'flight_info', 'pax_number',
    'boat_price', 'transport_price', 'transport_text', 'taxes_price', 'taxes_text',
    'discount_price', 'discount_text', 'extra_hour_price', 'extra_hour_text',
    'custom_price', 'custom_text', 'total_price',
  ],
  'confirmation-yacht': [
    'today_date', 'charter_date', 'client_name', 'charterer',
    'brand_of_vessel', 'name_of_vessel', 'length', 'cruising_area',
    'from_hour', 'to_hour', 'pax_number',
    'boat_price', 'transport_price', 'taxes_price', 'discount_price',
    'extra_hour_price', 'custom_price', 'total_price',
  ],
  'facturation-yacht': [
    'today_date', 'charter_date', 'client_name', 'charterer', 'invoice_number',
    'brand_of_vessel', 'name_of_vessel', 'length',
    'boat_price', 'transport_price', 'taxes_price', 'discount_price',
    'extra_hour_price', 'custom_price', 'total_price',
  ],
  'contrat-seabob': [
    'client_name', 'charter_date', 'spot',
    'seabob_number', 'seabob_duration', 'seabob_price',
    'esurf_line', 'total_price',
  ],
  'confirmation-seabob': [
    'client_name', 'charter_date',
    'seabob_number', 'seabob_duration', 'seabob_price',
    'esurf_description', 'esurf_price_display',
    'additional_description', 'additional_price_display', 'total_price',
  ],
  'facturation-seabob': [
    'client_name', 'charter_date', 'invoice_number', 'charterer',
    'seabob_number', 'seabob_duration', 'seabob_price',
    'esurf_description', 'esurf_price_display',
    'additional_description', 'additional_price_display', 'total_price',
  ],
  'contrat-jetski': [
    'client_name', 'charter_date',
    'jetski_number', 'jetski_duration', 'jetski_price',
  ],
  'confirmation-jetski': [
    'client_name', 'charter_date',
    'jetski_number', 'jetski_duration', 'jetski_price',
  ],
  'facturation-jetski': [
    'client_name', 'charter_date',
    'jetski_number', 'jetski_duration', 'jetski_price',
  ],
  'contrat-lunch': [
    'client_name', 'charter_date', 'menu_text', 'menu_price',
    'additional_description', 'additional_price_display', 'total_price',
  ],
  'confirmation-lunch': [
    'client_name', 'charter_date', 'menu_text', 'menu_price',
    'additional_description', 'additional_price_display', 'total_price',
  ],
  'facture-lunch': [
    'client_name', 'charter_date', 'invoice_number', 'menu_text', 'menu_price',
    'additional_description', 'additional_price_display', 'total_price',
  ],
  'contrat-custom': [
    'client_name', 'charter_date', 'custom_text', 'custom_price', 'total_price',
  ],
};

/**
 * Validate that a Google Docs template still contains all required {{variables}}.
 * If variables are missing, restore from Supabase backup.
 */
export const validateTemplate = async (templateKey, templateId) => {
  const requiredVars = REQUIRED_VARIABLES[templateKey];
  if (!requiredVars) return { valid: true, missing: [] };

  try {
    console.log(`Validating template: ${templateKey} (${templateId})`);
    const text = await getDocumentText(templateId);

    const missing = requiredVars.filter(v => !text.includes(`{{${v}}}`));

    if (missing.length === 0) {
      console.log(`Template ${templateKey} is valid`);
      return { valid: true, missing: [] };
    }

    console.warn(`Template ${templateKey} is MISSING ${missing.length} variables:`, missing);

    // Try to restore from Supabase backup
    const restored = await restoreTemplateFromBackup(templateKey, templateId, missing);
    if (restored) {
      console.log(`Template ${templateKey} restored from backup`);
      return { valid: true, missing: [], restored: true };
    }

    return { valid: false, missing };
  } catch (error) {
    console.error(`Template validation failed for ${templateKey}:`, error.message);
    // Don't block generation if validation fails — just warn
    return { valid: true, missing: [], error: error.message };
  }
};

/**
 * Backup a template by copying it as a Google Doc in Drive.
 * Stores the backup doc ID in localStorage so we can restore later.
 */
export const backupTemplate = async (templateKey, templateId) => {
  try {
    console.log(`[Backup] ▶ Starting: ${templateKey} (templateId: ${templateId})`);

    const backupIds = JSON.parse(localStorage.getItem(BACKUP_IDS_KEY) || '{}');
    console.log(`[Backup] Current backup IDs in localStorage:`, backupIds);

    if (backupIds[templateKey]) {
      console.log(`[Backup] Found existing backup for ${templateKey} → deleting ${backupIds[templateKey]}`);
      try {
        await deleteFile(backupIds[templateKey]);
        console.log(`[Backup] Old backup deleted ✓`);
      } catch (e) {
        console.warn(`[Backup] Could not delete old backup:`, e.message);
      }
    }

    const backupFolderId = await getOrCreateBackupFolder();
    console.log(`[Backup] Target folder: ${backupFolderId}`);

    const randomId = Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    console.log(`[Backup] Copying doc as: "${randomId}"`);
    const copied = await copyGoogleDoc(templateId, randomId, backupFolderId);
    console.log(`[Backup] Copy created ✓ → new doc id: ${copied.id}`);

    backupIds[templateKey] = copied.id;
    localStorage.setItem(BACKUP_IDS_KEY, JSON.stringify(backupIds));
    console.log(`[Backup] Saved to localStorage ✓`, backupIds);

    return true;
  } catch (error) {
    console.error(`[Backup] ✗ Failed for ${templateKey}:`, error);
    return false;
  }
};

/**
 * Restore a template by copying the backup Google Doc back over the original.
 * This preserves full formatting since we're working with real Google Docs.
 */
const restoreTemplateFromBackup = async (templateKey, templateId, missingVars) => {
  try {
    const backupIds = JSON.parse(localStorage.getItem(BACKUP_IDS_KEY) || '{}');
    const backupId = backupIds[templateKey];

    if (!backupId) {
      console.warn(`[Restore] No backup found for ${templateKey}. Run backupTemplate() first.`);
      return false;
    }

    console.log(`[Restore] Restoring ${templateKey} from backup doc ${backupId}`);

    // Copy the backup doc content into the original template doc using replaceAllText
    // Strategy: read variables from backup doc and re-insert missing ones into original
    const backupText = await getDocumentText(backupId);
    const currentText = await getDocumentText(templateId);
    const replacements = [];

    for (const varName of missingVars) {
      const placeholder = `{{${varName}}}`;
      const idx = backupText.indexOf(placeholder);
      if (idx === -1) continue;

      const before = backupText.substring(Math.max(0, idx - 50), idx);

      let anchor = '';
      for (let len = before.length; len >= 5; len--) {
        const candidate = before.substring(before.length - len).trim();
        if (candidate && currentText.includes(candidate)) {
          anchor = candidate;
          break;
        }
      }

      if (anchor) {
        replacements.push({ find: anchor, replace: `${anchor}${placeholder}` });
      }
    }

    if (replacements.length > 0) {
      await replaceTextInDoc(templateId, replacements);
      console.log(`[Restore] Restored ${replacements.length}/${missingVars.length} variables in ${templateKey}`);
      return true;
    }

    console.warn(`[Restore] Could not find anchors to restore variables in ${templateKey}`);
    return false;
  } catch (error) {
    console.error(`[Restore] Failed for ${templateKey}:`, error.message);
    return false;
  }
};

/**
 * Get template ID based on document type and contract type
 */
const getTemplateId = (type, contractType) => {
  let templateKey;

  if (contractType === 'bateaux') {
    const yachtTemplates = {
      'Contrat': 'contrat-yacht',
      'Confirmation': 'confirmation-yacht',
      'Facturation': 'facturation-yacht',
    };
    templateKey = yachtTemplates[type] || `${type.toLowerCase()}-yacht`;
  } else if (contractType === 'seabob') {
    const seabobTemplates = {
      'Contrat': 'contrat-seabob',
      'Confirmation': 'confirmation-seabob',
      'Facturation': 'facturation-seabob',
    };
    templateKey = seabobTemplates[type] || `${type.toLowerCase()}-seabob`;
  } else if (contractType === 'jetski') {
    const jetskiTemplates = {
      'Contrat': 'contrat-jetski',
      'Confirmation': 'confirmation-jetski',
      'Facturation': 'facturation-jetski',
    };
    templateKey = jetskiTemplates[type] || `${type.toLowerCase()}-jetski`;
  } else if (contractType === 'lunch') {
    const lunchTemplates = {
      'Contrat': 'contrat-lunch',
      'Confirmation': 'confirmation-lunch',
      'Facture': 'facture-lunch',
      'Facturation': 'facture-lunch',
    };
    templateKey = lunchTemplates[type] || `${type.toLowerCase()}-lunch`;
  } else if (contractType === 'custom') {
    const customTemplates = {
      'Contrat': 'contrat-custom',
      'Confirmation': 'confirmation-custom',
      'Facturation': 'facturation-custom',
    };
    templateKey = customTemplates[type] || `${type.toLowerCase()}-custom`;
  } else {
    templateKey = `${type.toLowerCase()}-${contractType.toLowerCase()}`;
  }

  const templateId = TEMPLATE_IDS[templateKey];

  if (!templateId) {
    throw new Error(`Template not found for: ${templateKey}. Please configure REACT_APP_GOOGLE_TEMPLATE_${templateKey.toUpperCase().replace(/-/g, '_')}`);
  }

  return templateId;
};

/**
 * Inject computed conditional fields into lunch data before building replacements
 */
const enrichLunchData = (data) => {
  const enriched = { ...data };

  const additionalText = enriched.additional_text || '';
  const additionalPrice = parseFloat(enriched.additional_price) || 0;

  const hasAdditional = additionalText.trim() !== '' || additionalPrice > 0;

  // New conditional placeholders
  enriched.additional_description = hasAdditional ? additionalText : '';
  enriched.additional_price_display = hasAdditional ? `${additionalPrice}€` : '';

  // Also clear legacy placeholders so old templates don't show 0€
  if (!hasAdditional) {
    enriched.additional_text = '';
    enriched.additional_price = '';
  }

  return enriched;
};

/**
 * Inject computed conditional fields into seabob data before building replacements
 */
const enrichSeabobData = (data) => {
  const enriched = { ...data };

  // E-surf conditional row
  const esurfNumber = enriched.esurf_number || enriched['e-surf_number'] || '';
  const esurfDuration = enriched.esurf_duration || '';
  const esurfPrice = enriched.esurf_price || enriched['e-surf_price'] || '';
  const hasEsurf = esurfNumber && esurfNumber !== '0' && esurfNumber !== '';

  // For table templates (Facturation): two separate cell placeholders
  enriched.esurf_description = hasEsurf
    ? `Esurf: ${esurfNumber}   Surf duration: ${esurfDuration}`
    : '';
  enriched.esurf_price_display = hasEsurf ? esurfPrice : '';

  // For plain text templates (Contrat): single full-line placeholder
  enriched.esurf_line = hasEsurf
    ? `Esurf: ${esurfNumber} - Duration: ${esurfDuration} - Price: ${esurfPrice}`
    : '';

  // Legacy block placeholder
  enriched.esurf_block = hasEsurf
    ? `Esurf: ${esurfNumber}   Surf duration: ${esurfDuration}\n${esurfPrice}`
    : '';

  // Additional row conditional (note: seabob uses "additional_row" + "addtional_price")
  const additionalRow = enriched.additional_row || '';
  const additionalPrice = parseFloat(enriched.addtional_price) || 0;
  const hasAdditional = additionalRow.trim() !== '' || additionalPrice > 0;

  enriched.additional_description = hasAdditional ? additionalRow : '';
  enriched.additional_price_display = hasAdditional ? `${additionalPrice}€` : '';

  return enriched;
};

const VESSEL_REGISTRY = {
  'MOH': { flag: 'SVG (St Vincent)', port_of_registry: 'SVG (St Vincent)' },
};
const VESSEL_REGISTRY_DEFAULT = { flag: 'FRANCE', port_of_registry: 'PAP' };

const enrichVesselData = (data) => {
  const vesselName = (data.name_of_vessel || '').trim().toUpperCase();
  const registry = VESSEL_REGISTRY[vesselName] || VESSEL_REGISTRY_DEFAULT;
  return { ...data, flag: registry.flag, port_of_registry: registry.port_of_registry };
};

/**
 * Build replacements array from document data
 */
const buildReplacements = (data, contractType) => {
  const replacements = [];

  // Enrich data with conditional blocks per contract type
  let enrichedData = enrichVesselData(data);
  if (contractType === 'seabob') enrichedData = enrichSeabobData(enrichedData);
  if (contractType === 'lunch') enrichedData = enrichLunchData(enrichedData);

  // Add all data fields as replacements
  Object.entries(enrichedData).forEach(([key, value]) => {
    // Skip complex objects
    if (typeof value === 'object' && value !== null) {
      return;
    }

    // Format the value as string
    let formattedValue = value !== null && value !== undefined ? String(value) : '';

    replacements.push({
      find: `{{${key}}}`,
      replace: formattedValue,
    });
  });

  return replacements;
};

/**
 * Generate file name for the document
 */
const generateFileName = (type, data, contractType) => {
  const clientName = (data.client_name || 'Client')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');

  const charterDate = data.charter_date
    ? data.charter_date.replace(/\//g, '-')
    : '';

  const timestamp = Date.now();

  if (contractType === 'bateaux') {
    const vesselName = data.vessel_name
      ? data.vessel_name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '')
      : (data.brand_of_vessel && data.length
        ? `${data.brand_of_vessel}_${data.length}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '')
        : 'Vessel');
    return `${type}_${clientName}_${vesselName}_${charterDate}_${timestamp}`;
  } else if (contractType === 'seabob') {
    return `${type}_${clientName}_Seabob_${charterDate}_${timestamp}`;
  } else if (contractType === 'jetski') {
    return `${type}_${clientName}_Jetski_${charterDate}_${timestamp}`;
  } else if (contractType === 'custom') {
    return `${type}_${clientName}_Custom_${charterDate}_${timestamp}`;
  } else {
    return `${type}_${clientName}_${charterDate}_${timestamp}`;
  }
};

/**
 * Main function to generate document using Google Drive
 *
 * @param {string} type - Document type (Contrat, Confirmation, Facturation)
 * @param {object} data - Data to fill in the template
 * @param {string} charterId - Optional charter ID
 * @param {string} contractType - Contract type (bateaux, seabob, jetski, lunch)
 * @returns {object} - Document record with URLs
 */
export const generateDocumentWithGoogleDrive = async (type, data, charterId = null, contractType = 'bateaux') => {
  console.log('=== GOOGLE DRIVE DOCUMENT GENERATION ===');
  console.log('Type:', type);
  console.log('Contract Type:', contractType);
  console.log('Charter ID:', charterId);
  console.log('Data:', JSON.stringify(data, null, 2));

  // Check authentication
  if (!isGoogleAuthenticated()) {
    const authUrl = getGoogleAuthUrl();
    throw new Error(`NOT_AUTHENTICATED:${authUrl}`);
  }

  try {
    // 1. Get template ID
    const templateId = getTemplateId(type, contractType);
    console.log('Template ID:', templateId);

    // 1b. Validate template variables (auto-restore if missing)
    // Template validation disabled


    // 2. Build replacements
    const replacements = buildReplacements(data, contractType);
    console.log('Replacements count:', replacements.length);

    // 3. Generate file name
    const fileName = generateFileName(type, data, contractType);
    console.log('File name:', fileName);

    // 4. Generate document from template
    const result = await generateDocumentFromTemplate({
      templateId,
      documentTitle: fileName,
      replacements,
      folderId: OUTPUT_FOLDER_ID,
      exportToPdf: true,
    });

    console.log('Document generated:', result.documentId);
    console.log('Document URL:', result.documentUrl);

    // 5. Save PDF locally if generated
    if (result.pdfBlob) {
      saveAs(result.pdfBlob, `${fileName}.pdf`);
      console.log('PDF downloaded locally');
    }

    // 6. Save document record to Supabase
    console.log('Saving to Supabase...');
    const { data: dbRecord, error: dbError } = await supabase
      .from('documents')
      .insert({
        type: type,
        client_name: data.client_name,
        charter_date: data.charter_date,
        charter_id: charterId,
        category: contractType,
        document_data: data,
        // Store Google Drive URLs instead of Supabase Storage URLs
        docx_url: result.documentUrl, // Google Doc view link
        pdf_url: result.documentUrl, // Same link (can export to PDF from there)
        google_doc_id: result.documentId,
      })
      .select()
      .single();

    if (dbError) {
      console.error('Error saving to Supabase:', dbError);
      throw dbError;
    }

    console.log('Saved to Supabase, ID:', dbRecord.id);
    console.log('=== GENERATION COMPLETE ===');

    return {
      ...dbRecord,
      googleDocId: result.documentId,
      googleDocUrl: result.documentUrl,
      pdfBlob: result.pdfBlob,
    };

  } catch (error) {
    console.error('=== ERROR IN GOOGLE DRIVE GENERATION ===');
    console.error('Error:', error.message);
    console.error('Stack:', error.stack);
    throw error;
  }
};

/**
 * Check if Google Drive is configured and authenticated
 */
export const checkGoogleDriveStatus = () => {
  const hasClientId = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const hasClientSecret = !!process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
  const isAuthenticated = isGoogleAuthenticated();

  return {
    configured: hasClientId && hasClientSecret,
    authenticated: isAuthenticated,
    authUrl: !isAuthenticated ? getGoogleAuthUrl() : null,
  };
};

/**
 * Regenerate a document from template without inserting a new Supabase row.
 * Returns the new Google Doc info for the caller to update the existing row.
 *
 * @param {string} type - Document type (Contrat, Confirmation, Facturation)
 * @param {object} data - Data to fill in the template
 * @param {string} charterId - Charter ID
 * @param {string} contractType - Contract type (bateaux, seabob, jetski, lunch, custom)
 * @returns {{ googleDocId: string, documentUrl: string, pdfBlob: Blob|null }}
 */
export const regenerateDocument = async (type, data, contractType = 'bateaux') => {
  if (!isGoogleAuthenticated()) {
    const authUrl = getGoogleAuthUrl();
    throw new Error(`NOT_AUTHENTICATED:${authUrl}`);
  }

  const templateId = getTemplateId(type, contractType);

  // Validate template before regeneration
  // Template validation disabled


  const replacements = buildReplacements(data, contractType);
  const fileName = generateFileName(type, data, contractType);

  const result = await generateDocumentFromTemplate({
    templateId,
    documentTitle: fileName,
    replacements,
    folderId: OUTPUT_FOLDER_ID,
    exportToPdf: true,
  });

  if (result.pdfBlob) {
    saveAs(result.pdfBlob, `${fileName}.pdf`);
  }

  return {
    googleDocId: result.documentId,
    documentUrl: result.documentUrl,
    pdfBlob: result.pdfBlob,
  };
};

/**
 * Backup ALL configured templates to Supabase Storage in one go.
 * Returns { success: number, failed: number, results: [] }
 */
export const backupAllTemplates = async () => {
  const results = [];
  let success = 0;
  let failed = 0;

  for (const [key, templateId] of Object.entries(TEMPLATE_IDS)) {
    if (!templateId) continue;
    try {
      await backupTemplate(key, templateId);
      results.push({ key, status: 'ok' });
      success++;
    } catch (error) {
      results.push({ key, status: 'error', error: error.message });
      failed++;
    }
  }

  return { success, failed, results };
};

export default {
  generateDocumentWithGoogleDrive,
  regenerateDocument,
  checkGoogleDriveStatus,
  validateTemplate,
  backupTemplate,
  backupAllTemplates,
};
